import { Flex } from "antd";
import dayjs from "dayjs";
import { FC, useMemo, useState } from "react";
import { backendDateFormat, displayDateFormat } from "tm-shared/constants";
import { OrderType, PaymentForm } from "tm-shared/types";
import { formatDate } from "tm-shared/utils";

import { useMultiTap } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { AppSelect } from "@/shared/forms";
import {
    changeCheckoutForm,
    changeCheckoutShipmentDate,
} from "@/store/slices/work/work.slice";

import {
    StyledCheckoutSection,
    StyledPaymentSwitch,
    StyledSectionText,
    StyledSectionTitle,
} from "./Styled";

const disabledFormChangeOrderTypes = [OrderType.P, OrderType.R];

interface CheckoutPaymentInfoProps {
    orderId: string;
    orderType: OrderType;
    creationDate: string;
    shipmentDate: string;
    customShipmentDate: string;
    paymentDate: string;
    paymentForm: PaymentForm;
    closed: boolean;
}

export const CheckoutPaymentInfo: FC<CheckoutPaymentInfoProps> = ({
    orderId,
    orderType,
    creationDate,
    shipmentDate,
    customShipmentDate,
    paymentDate,
    paymentForm,
    closed,
}) => {
    const dispatch = useAppDispatch();
    const [showDateSelect, setShowDateSelect] = useState(false);

    const hasCustomDate = customShipmentDate && customShipmentDate !== "-";

    const dateOptions = useMemo(() => {
        if (!shipmentDate) return [];
        const baseDate = dayjs(shipmentDate);

        return Array.from({ length: 7 }, (_, i) => {
            const date = baseDate.add(i, "day");
            return {
                value: date.format(backendDateFormat.date),
                label: date.format(displayDateFormat.date),
            };
        });
    }, [shipmentDate]);

    const handleDoubleClick = () => {
        if (closed || orderType === OrderType.R) return;
        setShowDateSelect(true);
    };

    const handleDateChange = (value: string) => {
        dispatch(changeCheckoutShipmentDate({ orderId, date: value }));
        setShowDateSelect(false);
    };

    const handleTap = useMultiTap(handleDoubleClick);

    return (
        <>
            <StyledCheckoutSection>
                <Flex justify="space-between" align="center" gap={24}>
                    <Flex vertical gap={6} flex={1}>
                        <StyledSectionTitle>Дата замовлення</StyledSectionTitle>
                        <StyledSectionText>
                            {formatDate(creationDate, "-")}
                        </StyledSectionText>
                    </Flex>

                    {orderType !== OrderType.R && (
                        <Flex
                            vertical
                            gap={6}
                            flex={1}
                            onTouchStart={handleTap}
                        >
                            <StyledSectionTitle>
                                Дата поставки
                            </StyledSectionTitle>

                            {showDateSelect ? (
                                <AppSelect
                                    value={
                                        hasCustomDate
                                            ? customShipmentDate
                                            : shipmentDate
                                    }
                                    options={dateOptions}
                                    onChange={handleDateChange}
                                    onBlur={() => setShowDateSelect(false)}
                                    autoFocus
                                    fullWidth
                                    size="middle"
                                />
                            ) : (
                                <StyledSectionText>
                                    {hasCustomDate
                                        ? formatDate(customShipmentDate)
                                        : formatDate(shipmentDate)}
                                </StyledSectionText>
                            )}
                        </Flex>
                    )}
                </Flex>
            </StyledCheckoutSection>

            {orderType !== OrderType.R && (
                <StyledCheckoutSection>
                    <Flex justify="space-between" align="center" gap={24}>
                        <Flex vertical gap={6} flex={1}>
                            <StyledSectionTitle>Дата оплати</StyledSectionTitle>
                            <StyledSectionText>{paymentDate}</StyledSectionText>
                        </Flex>

                        <Flex vertical gap={6} flex={1}>
                            <StyledSectionTitle>Тип оплати</StyledSectionTitle>

                            <StyledPaymentSwitch
                                className="switch-form"
                                checkedChildren="Форма 2"
                                unCheckedChildren="Форма 1"
                                checked={paymentForm === PaymentForm.Cash2}
                                onChange={() =>
                                    dispatch(changeCheckoutForm(orderId))
                                }
                                disabled={
                                    closed ||
                                    disabledFormChangeOrderTypes.includes(
                                        orderType,
                                    )
                                }
                            />
                        </Flex>
                    </Flex>
                </StyledCheckoutSection>
            )}
        </>
    );
};
